import { motion } from 'framer-motion';
import { useLanguage } from '../../../contexts/LanguageContext';
// V4-CONSTANTS-AUDIT-FRONTEND 2026-05-28
import { formatMonthYear } from '../../../utils/dateUtils';

interface QuotaUsageBarProps {
  percentage: number;
  period: string;
  compact?: boolean;
  /** Label personnalisé remplaçant "Crédits AI" */
  label?: string;
  /** Texte de détail personnalisé (ex: "3 / 10 images") */
  detail?: string;
  /** Grise la barre entière (ex: fonctionnalité indisponible) */
  disabled?: boolean;
  /** Badge affiché quand disabled (ex: "Bientôt disponible") */
  disabledLabel?: string;
}

/**
 * Barre de progression des crédits AI avec code couleur.
 * 0-60% vert, 60-80% ambre, 80-95% orange, 95%+ rouge.
 * Supporte un mode disabled pour les fonctionnalités à venir.
 */
export function QuotaUsageBar({
  percentage,
  period,
  compact = false,
  label,
  detail,
  disabled = false,
  disabledLabel,
}: QuotaUsageBarProps) {
  const { t } = useLanguage();

  const getColor = (pct: number) => {
    if (pct >= 95) return { bar: '#EF4444', bg: 'rgba(239, 68, 68, 0.15)', text: 'text-red-500' };
    if (pct >= 80) return { bar: '#F97316', bg: 'rgba(249, 115, 22, 0.15)', text: 'text-orange-500' };
    if (pct >= 60) return { bar: '#F59E0B', bg: 'rgba(245, 158, 11, 0.15)', text: 'text-amber-500' };
    return { bar: '#10B981', bg: 'rgba(16, 185, 129, 0.15)', text: 'text-emerald-500' };
  };

  // Couleurs désactivées
  const disabledColors = {
    bar: '#9CA3AF',
    bg: 'rgba(156, 163, 175, 0.15)',
    text: 'text-gray-400',
  };

  const colors = disabled ? disabledColors : getColor(percentage);
  const clampedWidth = disabled ? 0 : Math.min(percentage, 100);

  // V4-CONSTANTS-AUDIT-FRONTEND 2026-05-28
  // Format period: "2026-02" → "Février 2026" via helper centralisé.
  const formatPeriod = (p: string) => {
    if (!p) return '';
    try {
      const [year, month] = p.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return formatMonthYear(date);
    } catch {
      return p;
    }
  };

  const displayLabel = label ?? (t.quota?.aiCredits ?? 'Crédits AI');

  if (compact) {
    return (
      <div className="w-full" style={{ opacity: disabled ? 0.5 : 1 }}>
        <div
          className="w-full h-1.5 rounded-full overflow-hidden"
          style={{ backgroundColor: colors.bg }}
        >
          <motion.div
            className="h-full rounded-full"
            style={{ backgroundColor: colors.bar }}
            initial={{ width: 0 }}
            animate={{ width: `${clampedWidth}%` }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          />
        </div>
        <p className={`text-xs mt-1 ${colors.text} font-medium`}>
          {disabled
            ? (disabledLabel ?? '')
            : `${percentage.toFixed(0)}% ${t.quota?.used ?? 'utilisé'}`}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-2" style={{ opacity: disabled ? 0.55 : 1 }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className="text-sm font-medium"
            style={{ color: disabled ? 'var(--color-text-secondary)' : 'var(--color-text-primary)' }}
          >
            {displayLabel}
          </span>
          {disabled && disabledLabel && (
            <span
              className="text-[10px] font-medium px-2 py-0.5 rounded-full"
              style={{
                backgroundColor: 'rgba(156, 163, 175, 0.2)',
                color: 'var(--color-text-secondary)',
              }}
            >
              {disabledLabel}
            </span>
          )}
        </div>
        {!disabled && (
          <span className={`text-sm font-semibold ${colors.text}`}>
            {percentage.toFixed(1)}%
          </span>
        )}
      </div>

      <div
        className="w-full h-2.5 rounded-full overflow-hidden"
        style={{ backgroundColor: colors.bg }}
      >
        <motion.div
          className="h-full rounded-full"
          style={{ backgroundColor: colors.bar }}
          initial={{ width: 0 }}
          animate={{ width: `${clampedWidth}%` }}
          transition={{ duration: 1, ease: 'easeOut' }}
        />
      </div>

      <div className="flex items-center justify-between">
        <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
          {disabled
            ? ''
            : (detail ?? `${percentage.toFixed(1)}% ${t.quota?.used ?? 'utilisé'}`)}
        </span>
        {!disabled && period && (
          <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            {formatPeriod(period)}
          </span>
        )}
      </div>

      {!disabled && percentage >= 80 && (
        <div
          className="text-xs px-3 py-1.5 rounded-lg mt-1"
          style={{
            backgroundColor: colors.bg,
            color: colors.bar,
          }}
        >
          {percentage >= 100
            ? (t.quota?.exceeded ?? 'Quota dépassé — renouvellement le 1er du mois prochain')
            : (t.quota?.warning ?? 'Attention : consommation élevée de crédits AI')}
        </div>
      )}
    </div>
  );
}
